import config from '@config';
import { generateKey, getClassName } from '@utils';
import { useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import type { ChartData } from 'types/common';
import Styled from './BaseChart.styled';

const defaultSpecs = {
  width: config.chart.regular.width,
  height: config.chart.regular.height,
  margin: {
    top: 0,
    right: 40,
    bottom: -14,
    left: 0
  },
  barSize: {
    min: 16,
    max: 20
  },
  tickSize: 2.5
};

export interface Props {
  average?: number;
  barSize?: number;
  className?: string;
  colors: readonly string[];
  data: ChartData[];
  domain: [number, number];
  gridHorizontal?: boolean;
  gridVertical?: boolean;
  xAxisTicks: number[];
  width?: number;
  height?: number;
  showYAxis?: boolean;
  overridenSpecs?: Partial<typeof defaultSpecs>;
  formatTick: (tick: number, index?: number) => string;
  formatLabel?: (value: string) => string | number;
  onSelectDataPoint?: (data: ChartData) => void;
}

function Chart({
  average,
  barSize: fixedBarSize = -1,
  className,
  colors,
  data,
  domain,
  gridHorizontal,
  gridVertical,
  xAxisTicks,
  width,
  height,
  showYAxis = false,
  overridenSpecs,
  formatTick,
  formatLabel,
  onSelectDataPoint
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const specs = { ...defaultSpecs, ...(overridenSpecs || {}) };
  const [chartWidth, setChartWidth] = useState(width || specs.width);

  // Calculate the height
  let chartHeight = (height || specs.height) + Math.abs(specs.margin.bottom);

  // Add the fixed number of pixels for perfect alignment with x-axis
  chartHeight += 2.5;

  // Calculate bar size
  let barSize = fixedBarSize;

  if (barSize === -1) {
    barSize = Math.round(chartHeight / data.length / 2);

    // Ensure bar size is always an even number
    if (barSize % 2 !== 0) {
      barSize -= 1;
    }

    if (barSize > specs.barSize.max) {
      barSize = specs.barSize.max;
    }

    if (barSize < specs.barSize.min || data.length < 4) {
      barSize = specs.barSize.min;
    }
  }

  const handleClick = async (data: ChartData, index: number) => {
    // Ensure the component's state is updated and the re-render
    // is over before making any changes to DOM
    await Promise.resolve(onSelectDataPoint?.(data));

    // Remove highlight from the previously selected bar & label
    const previouslySelected =
      wrapperRef.current.querySelectorAll('.highlight');

    if (previouslySelected) {
      for (const element of previouslySelected) {
        element.classList.remove('highlight');
      }
    }

    // Add highlight to the newly selected bar & label
    const currentlySelected = wrapperRef.current?.querySelectorAll(
      `.bar-${index + 1}, .bar-label-${index + 1}`
    );

    for (const element of currentlySelected) {
      element.classList.add('highlight');
    }
  };

  return (
    <Styled.Wrapper ref={wrapperRef}>
      <ResponsiveContainer
        width="100%"
        height={chartHeight}
        onResize={(nextWidth) => {
          if (nextWidth > 0) {
            setChartWidth(nextWidth);
          }
        }}>
        <BarChart
          className={getClassName(['chart', className])}
          data={data}
          layout="vertical"
          margin={{ ...specs.margin }}>
          <XAxis
            type="number"
            axisLine={false}
            domain={domain}
            fontSize={10}
            interval={0}
            padding={{ left: 0, right: 0 }}
            ticks={xAxisTicks}
            tick={{ fill: 'currentColor', fontFamily: 'Faktum' }}
            tickFormatter={formatTick}
            tickLine={false}
            tickSize={specs.tickSize}
          />
          <YAxis
            hide
            dataKey="name"
            type="category"
            tick={false}
            interval={0}
          />
          {showYAxis && (
            <ReferenceLine
              x={0}
              stroke="currentColor"
              strokeWidth={3}
              z={9999}
            />
          )}
          <Bar
            dataKey="value"
            barSize={barSize}
            radius={[0, 2, 2, 0]}
            onClick={handleClick}>
            {data.map((entry, index) => (
              <Cell
                className={getClassName({
                  bar: true,
                  [`bar-${index + 1}`]: true,
                  highlight: entry.isOrigin
                })}
                key={generateKey(`cell-${entry.name}`)}
                fill={colors.length > 1 ? colors[index] : colors[0]}
              />
            ))}
            <LabelList
              dataKey="value"
              content={({ y, value, index }) =>
                renderCustomLabel({
                  barSize,
                  chartWidth,
                  chartHeight,
                  className: getClassName({
                    'bar-label': true,
                    [`bar-label-${index + 1}`]: true,
                    highlight: data[index].isOrigin
                  }),
                  groupSize: data.length,
                  specs,
                  value: average ? Math.abs(Number(value) + average) : value,
                  yPosition: y,
                  formatLabel,
                  onClick: () => handleClick(data[index], index)
                })
              }
            />
          </Bar>
          {(typeof gridVertical === 'undefined' || gridVertical) && (
            <CartesianGrid
              className="cartesian-grid-line"
              stroke={'var(--chart-grid-color)'}
              strokeDasharray="2.5 2.5"
            />
          )}
          {/* Horizontal lines */}
          {(typeof gridHorizontal === 'undefined' || gridHorizontal) &&
            data.map((entry) => (
              <ReferenceLine
                key={generateKey(`refline-${entry.name}-${entry.suffix}`)}
                y={entry.name}
                stroke={'var(--chart-grid-color)'}
                strokeWidth={1}
                position="end"
                label={({ viewBox }) => (
                  <line
                    x1={viewBox.x}
                    y1={viewBox.y}
                    x2={viewBox.width + specs.margin.right}
                    y2={viewBox.y}
                    stroke={'var(--chart-grid-color)'}
                    strokeWidth={1}
                  />
                )}
              />
            ))}
          {/* Left border of the label list */}
          <ReferenceLine
            x={xAxisTicks.at(-1)}
            stroke={'var(--chart-grid-color)'}
          />
          {/* Center the reference line at the adjusted 0 (average) */}
          {average && (
            <ReferenceLine x={0} stroke="currentColor" strokeWidth={2} />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Styled.Wrapper>
  );
}

const renderCustomLabel = ({
  barSize,
  chartWidth,
  chartHeight,
  className,
  groupSize,
  specs,
  value,
  yPosition,
  formatLabel,
  onClick
}) => {
  // Calculate values dynamically
  const rectWidth = specs.margin.right;
  const rectHeight = Math.round(chartHeight / groupSize - groupSize);

  return (
    <g className={className} onPointerDown={onClick}>
      <rect
        x={chartWidth - rectWidth}
        y={Math.round(yPosition + (barSize - rectHeight) / 2)}
        width={specs.margin.right}
        height={Math.round(rectHeight)}
        fill="transparent"
      />
      <text
        x={Math.round(chartWidth - rectWidth / 2)}
        y={Math.round(yPosition + barSize / 2)}
        fill="currentColor"
        fontSize={12}
        fontFamily="Faktum"
        textAnchor="middle"
        dominantBaseline="central">
        {formatLabel?.(value) || value}
      </text>
    </g>
  );
};

export default Chart;
